import React, { useState, useEffect } from "react";
import axios from "axios";
import { Users, UserCheck, UserX, Clock, TrendingUp, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const [stats, setStats] = useState({
    total_workers: 0,
    present_today: 0,
    absent_today: 0,
    absent_scanned: 0,
    absent_general: 0,
    late_today: 0,
    on_time_today: 0,
    attendance_rate: 0
  });
  const [todayAttendance, setTodayAttendance] = useState([]);
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, attendanceRes, historyRes] = await Promise.all([
        axios.get(`${API}/stats/today`),
        axios.get(`${API}/attendance/today`),
        axios.get(`${API}/stats/history?days=7`)
      ]);
      setStats(statsRes.data);
      setTodayAttendance(attendanceRes.data);
      setHistoryData(historyRes.data);
      setLoading(false);
    } catch (error) {
      toast.error("Erreur de chargement des donnees");
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Page header */}
      <div className="bg-white border-b border-slate-200 px-4 sm:px-6 lg:px-8 py-5">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl font-manrope font-bold text-slate-900 tracking-tight" data-testid="dashboard-title">Tableau de Bord</h1>
          <p className="mt-1 text-sm text-slate-500">{new Date().toLocaleDateString("fr-FR", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</p>
        </div>
      </div>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

      {/* Ligne 1 : 4 stats principales */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
        <Card data-testid="stat-total-workers">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div><p className="text-sm font-medium text-slate-600">Total Ouvriers</p><p className="text-3xl font-bold text-slate-900 mt-2">{stats.total_workers}</p></div>
              <div className="bg-blue-50 p-3 rounded-lg"><Users className="h-6 w-6 text-blue-600" /></div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-on-time">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div><p className="text-sm font-medium text-slate-600">A l'heure</p><p className="text-3xl font-bold text-slate-900 mt-2">{stats.on_time_today}</p></div>
              <div className="bg-emerald-50 p-3 rounded-lg"><UserCheck className="h-6 w-6 text-emerald-600" /></div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-late">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div><p className="text-sm font-medium text-slate-600">Retards</p><p className="text-3xl font-bold text-slate-900 mt-2">{stats.late_today}</p></div>
              <div className="bg-amber-50 p-3 rounded-lg"><Clock className="h-6 w-6 text-amber-600" /></div>
            </div>
          </CardContent>
        </Card>

      </div>

      {/* Ligne 2 : 2 catégories absents */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <Card className="border-l-4 border-l-rose-400">
          <CardContent className="pt-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-rose-600">Absents Scannes (trop tard)</p>
                <p className="text-3xl font-bold text-rose-700 mt-1">{stats.absent_scanned}</p>
                <p className="text-xs text-slate-500 mt-1">Ont scanne mais apres la limite</p>
              </div>
              <div className="bg-rose-100 p-3 rounded-lg"><AlertTriangle className="h-6 w-6 text-rose-500" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-slate-400">
          <CardContent className="pt-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Absents Generaux (pas scannes)</p>
                <p className="text-3xl font-bold text-slate-700 mt-1">{stats.absent_general}</p>
                <p className="text-xs text-slate-500 mt-1">N'ont pas scanne aujourd'hui</p>
              </div>
              <div className="bg-slate-100 p-3 rounded-lg"><UserX className="h-6 w-6 text-slate-500" /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card>
          <CardHeader><CardTitle className="flex items-center"><TrendingUp className="h-5 w-5 mr-2 text-orange-500" />Taux de Presence</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center justify-center h-48">
              <div className="text-center">
                <div className="text-6xl font-bold text-slate-900">{stats.attendance_rate.toFixed(1)}%</div>
                <p className="text-slate-600 mt-2">Taux de presence aujourd'hui</p>
                <div className="flex justify-center gap-4 mt-4 text-sm">
                  <span className="text-emerald-600 font-semibold">✅ {stats.on_time_today} a l'heure</span>
                  <span className="text-amber-600 font-semibold">⚠️ {stats.late_today} retards</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Historique 7 Derniers Jours</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={historyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tickFormatter={d => new Date(d).toLocaleDateString("fr-FR", {day:"2-digit",month:"2-digit"})} stroke="#64748b" style={{fontSize:"12px"}} />
                <YAxis stroke="#64748b" style={{fontSize:"12px"}} />
                <Tooltip labelFormatter={d => new Date(d).toLocaleDateString("fr-FR")} contentStyle={{background:"#fff",border:"1px solid #e2e8f0",borderRadius:"8px"}} />
                <Line type="monotone" dataKey="count" stroke="#f97316" strokeWidth={2} dot={{fill:"#f97316",r:4}} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Pointages d'aujourd'hui</CardTitle>
            {todayAttendance.length > 10 && (
              <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded-full">{todayAttendance.length} total</span>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="attendance-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">Matricule</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">Nom</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide hidden sm:table-cell">Département</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">Heure</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">Statut</th>
                </tr>
              </thead>
              <tbody>
                {todayAttendance.length === 0 ? (
                  <tr><td colSpan="5" className="text-center py-12 text-slate-400">Aucun pointage aujourd'hui</td></tr>
                ) : (showAll ? todayAttendance : todayAttendance.slice(0, 10)).map((record, index) => {
                  const [h, m] = record.time.split(":").map(Number);
                  const totalMin = h * 60 + m;
                  const weekday = new Date().getDay();
                  const isWeekend = weekday === 0 || weekday === 6;
                  const onTimeLimit = isWeekend ? (14*60+9) : (15*60+33);
                  const lateLimit = isWeekend ? (14*60+49) : (16*60+9);
                  const isAbsent = totalMin > lateLimit;
                  const isLate = !isAbsent && totalMin > onTimeLimit;
                  const statusLabel = isAbsent ? "Absent scanné" : isLate ? "Retard" : "À l'heure";
                  const statusStyle = isAbsent ? "bg-rose-100 text-rose-700" : isLate ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700";
                  return (
                    <tr key={index} className={"border-b border-slate-100 transition-colors " + (index % 2 === 0 ? "bg-white" : "bg-slate-50/50") + " hover:bg-orange-50/30"} data-testid={`attendance-row-${index}`}>
                      <td className="py-3 px-4 text-sm font-mono font-semibold text-slate-800">{record.matricule}</td>
                      <td className="py-3 px-4 text-sm text-slate-900 font-medium">{record.worker_name}</td>
                      <td className="py-3 px-4 text-sm text-slate-500 hidden sm:table-cell">{record.department_name || "—"}</td>
                      <td className="py-3 px-4 text-sm font-mono text-slate-700">{record.time.substring(0, 5)}</td>
                      <td className="py-3 px-4 text-sm"><span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${statusStyle}`}>{statusLabel}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {todayAttendance.length > 10 && (
            <div className="mt-4 text-center border-t border-slate-100 pt-4">
              <button onClick={() => setShowAll(v => !v)}
                className="inline-flex items-center gap-2 text-sm font-semibold text-orange-600 hover:text-orange-700 transition-colors">
                {showAll ? <><ChevronUp className="h-4 w-4" />Réduire</> : <><ChevronDown className="h-4 w-4" />Voir tout ({todayAttendance.length} pointages)</>}
              </button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
    </div>
  );
}
